import React, { useEffect, useMemo } from 'react';
import { actions } from '../redux/brokerActions';
import { Box, Button, Loader } from '../../../common/components';
import InternalContainer from '../../layout/containers/Internal';
import BrokerShow from '../components/BrokerShow/BrokerShow';
import BrokerContracts from '../components/BrokerContracts/BrokerContracts';
import BrokerSources from '../../broker/BrokerSources/BrokerSources';
import { connect, useSelector } from 'react-redux';
import BrokerCommissionsShow from '../components/BrokerCommissionsShow/BrokerCommissionsShow';
import BrokerUsers from '../components/BrokerUsers/BrokerUsers';
import BrokerTheme from '../components/BrokerTheme/BrokerTheme';
import BrokerContent from '../components/BrokerContent/BrokerContent';
import BrokerNotes from '../components/BrokerNotes/BrokerNotes';
import BrokerAudit from '../components/BrokerAudit/BrokerAudit';
import getIncludedResource from '../../../helpers/getIncludedResource';
import InternalTabs from '../../layout/components/InternalTabs/InternalTabs';
import BrokerEmails from '../components/BrokerEmails/BrokerEmails';
import BrokerLabel from '../components/BrokerLabel/BrokerLabel';
import BrokerToba from '../components/BrokerToba/BrokerToba';

const TAB_NAME = 'brokerTabs';

const getTobas = (documents, channelName, includedResources, travelProductId) => {
  return Array.isArray(documents)
    ? documents?.reduce(
        (carry, document) => {
          if (
            document.attributes?.document_type === 'Terms of Business Customer Agreement'
          ) {
            if (document.attributes.status === 'active') {
              carry['active'] = {
                data: {
                  ...document,
                  attributes: {
                    ...document.attributes,
                  },
                  relationships: {
                    ...document.relationships,
                    products: {
                      data: [
                        {
                          id: travelProductId,
                        },
                      ],
                    },
                  },
                },
                included: includedResources,
              };
            }

            carry['all'].push({
              id: document.id,
              name: document.attributes.name,
              display_name: document.attributes.display_name,
              short_name: document.attributes.short_name,
              version_number: document.attributes.version_number,
              from_date: document.attributes.from_date,
              to_date: document.attributes.to_date,
              file_path: document.attributes.file_path,
              type: document.attributes.type,
              is_replaced: document.attributes.replaced,
              status: document.attributes.status,
              channel_name: channelName,
              is_superseding: document?.relationships?.channel?.data?.id,
              channel_id: document?.relationships?.channel?.data?.id,
              schemes: [],
            });
          }
          return carry;
        },
        {
          active: undefined,
          all: [],
        },
      )
    : [];
};

const Container = ({ status, name, children }) => (
  <Box className={'tracking-link-show'}>
    <BrokerLabel status={status || ''} name={name} />
    {children}
  </Box>
);

const BrokerShowContainer = ({ getBroker, clearBroker, params: { id } }) => {
  useEffect(
    () => {
      getBroker(id);
      // actions.getAllProducts();
      return () => {
        clearBroker();
      };
    },
    [getBroker, clearBroker, id],
  );

  const resource = useSelector((state) => state.broker?.resources?.[id]);
  const includedResources = resource?.included;
  const channel = getIncludedResource(resource?.data, includedResources, 'channel');
  const documents = getIncludedResource(
    channel?.data,
    includedResources,
    'all_documents',
  );
  const channelProducts = getIncludedResource(
    channel?.data,
    includedResources,
    'products',
  );
  const channelProductId = channelProducts?.[0]?.id;
  const channelName = channel?.data?.attributes?.name;

  const tobas = useMemo(
    () => getTobas(documents, channelName, includedResources, channelProductId),
    [documents, channelName, includedResources, channelProductId],
  );

  const brokerType = resource?.data?.attributes?.broker_type ?? 'broker';
  if (!resource) {
    return (
      <InternalContainer
        title={brokerType === 'broker' ? 'Broker Branch' : 'Tracking Link'}
      >
        <Loader />
      </InternalContainer>
    );
  }

  const refresh = () => {
    return getBroker(id);
  };

  const { name, status } = resource?.data?.attributes ?? {};
  const hasWhiteLabel = channel.data.attributes !== undefined;
  const resourceName = hasWhiteLabel
    ? channel.data.attributes.name
    : name + ' (No Whitelabel)';

  const getPrimaryContact = (broker) => {
    const contacts = getIncludedResource(
      broker.data,
      broker.included,
      'primary_contacts',
    );
    if (contacts && contacts.length > 0) {
      return contacts[0];
    }
    return null;
  };

  return (
    <InternalContainer
      title={brokerType === 'broker' ? 'Broker Branch' : 'Tracking Link'}
      buttons={
        <Button
          bsStyle="default"
          label="Back"
          isBackButton={true}
          leftIcon="caret-left"
        />
      }
    >
      <InternalTabs
        id={TAB_NAME}
        tabs={[
          {
            permissions: ['broker.view'],
            title: 'Details',
            content: (
              <Container name={name} status={status}>
                <BrokerShow resource={resource} />
              </Container>
            ),
          },
          {
            permissions: ['user.view'],
            title: 'Users',
            content: (
              <Container name={name} status={status}>
                <BrokerUsers
                  brokerName={resource.data.attributes.name}
                  brokerId={resource.data.id}
                  primaryContact={getPrimaryContact(resource)}
                  organisation={getIncludedResource(
                    resource.data,
                    resource.included,
                    'organisation',
                  )}
                />
              </Container>
            ),
          },
          {
            permissions: ['broker.edit'],
            title: 'Insurers',
            content: (
              <Container name={name} status={status}>
                <BrokerCommissionsShow
                  resource={resource}
                  commissions={getIncludedResource(
                    resource.data,
                    resource.included,
                    'active_commissions',
                  )}
                />
              </Container>
            ),
          },
          {
            permissions: ['broker.view'],
            title: 'Sources',
            content: (
              <Container name={name} status={status}>
                <BrokerSources resource={resource} />
              </Container>
            ),
          },
          {
            permissions: ['broker.approve'],
            title: 'Contracts',
            content: (
              <Container name={name} status={status}>
                <BrokerContracts resource={resource} />
              </Container>
            ),
          },
          {
            permissions: ['broker.approve'],
            title: 'White Label',
            content: (
              <Container name={resourceName} status={status}>
                <BrokerTheme resource={resource} brokerId={resource.data.id} />
              </Container>
            ),
          },
          {
            permissions: ['broker.approve'],
            title: 'Email and Letter Content',
            content: hasWhiteLabel ? (
              <Container name={name} status={status}>
                <BrokerContent
                  resource={resource}
                  renewalsEnabled={resource.data.attributes.metadata?.renewals_enabled}
                  brokerId={resource.data.id}
                />
              </Container>
            ) : null,
          },
          {
            permissions: ['broker.edit'],
            title: 'TOBA',
            content:
              hasWhiteLabel && !!tobas ? (
                <Container name={name} status={status}>
                  <BrokerToba
                    resource={resource}
                    brokerId={resource.data.id}
                    tobas={tobas}
                    onRefresh={refresh}
                    wlChannelId={channel?.data?.id}
                    productId={channelProductId}
                    channelName={channelName}
                  />
                </Container>
              ) : null,
          },
          {
            permissions: ['communication.view'],
            title: 'Emails',
            content: (
              <Container name={name} status={status}>
                <BrokerEmails resource={resource} />
              </Container>
            ),
          },
          {
            permissions: ['broker.notes_view'],
            title: 'Notes',
            content: (
              <Container name={name} status={status}>
                <BrokerNotes resource={resource} />,
              </Container>
            ),
          },
          {
            permissions: ['system.audit_logs_view'],
            orgType: ['owner'],
            title: 'Audit',
            content: (
              <Container name={name} status={status}>
                <BrokerAudit resource={resource} />
              </Container>
            ),
          },
        ]}
      />
    </InternalContainer>
  );
};
export default connect(
  (state) => ({
    broker: state.broker,
  }),
  {
    getBroker: actions.getBroker,
    clearBroker: actions.clearBroker,
  },
)(BrokerShowContainer);
